import React from 'react';
import {
  Box,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { OutlineButton, StatusBadge } from '../../../components/ui';
import { formatDateTimeDisplay } from '../../../utils/personName';

function FieldCard({ label, value }) {
  return (
    <Box
      px={3}
      py={2}
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
      w="100%"
    >
      <Text fontSize="11px" color="text.secondary" mb={0.5} fontWeight="500" lineHeight="1.2">
        {label}
      </Text>
      <Text fontSize="14px" fontWeight="600" whiteSpace="pre-wrap" lineHeight="1.35">
        {value || '—'}
      </Text>
    </Box>
  );
}

function materialStatusLabel(status) {
  if (status === 'verified') return 'Verified';
  if (status === 'rejected') return 'Rejected';
  return 'Under review';
}

function statusBadgeProps(status) {
  if (status === 'Verified') return { status: 'verified' };
  if (status === 'Rejected') return { status: 'error' };
  return { status: 'pending' };
}

function formatDateTime(value) {
  return formatDateTimeDisplay(value);
}

export function MaterialSupplyDetailModal({ isOpen, onClose, material, onViewImage }) {
  if (!material) return null;

  const reviewLabel = materialStatusLabel(material.verificationStatus);
  const hasImage = Boolean(material.imageUrl);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      isCentered
      scrollBehavior="inside"
      blockScrollOnMount
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        my={4}
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0} pb={2}>
          Material details
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody
          flex="1 1 auto"
          overflowY="auto"
          minH={0}
          maxH="calc(90vh - 8.5rem)"
          pb={4}
          sx={{ overscrollBehavior: 'contain' }}
        >
          <VStack align="stretch" spacing={4}>
            <Box
              w="100%"
              h={{ base: '180px', md: '220px' }}
              borderRadius="md"
              overflow="hidden"
              bg="bg.input"
              borderWidth="1px"
              borderColor="border.subtle"
              cursor={hasImage ? 'pointer' : 'default'}
              onClick={() => {
                if (hasImage) onViewImage?.(material);
              }}
              role={hasImage ? 'button' : undefined}
              tabIndex={hasImage ? 0 : undefined}
              onKeyDown={(e) => {
                if (!hasImage) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onViewImage?.(material);
                }
              }}
            >
              {hasImage ? (
                <Image
                  src={material.imageUrl}
                  alt={material.name || 'Material'}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  fallback={
                    <Flex w="100%" h="100%" align="center" justify="center">
                      <Text fontSize="14px" color="text.secondary">
                        Image unavailable
                      </Text>
                    </Flex>
                  }
                />
              ) : (
                <Flex w="100%" h="100%" align="center" justify="center">
                  <Text fontSize="14px" color="text.secondary">
                    No image uploaded
                  </Text>
                </Flex>
              )}
            </Box>
            {hasImage ? (
              <Text fontSize="12px" color="text.secondary" mt={-2}>
                Tap image to open full preview
              </Text>
            ) : null}

            <Flex gap={2} wrap="wrap">
              <StatusBadge {...statusBadgeProps(reviewLabel)}>{reviewLabel}</StatusBadge>
              <StatusBadge status={material.isActive ? 'verified' : 'error'}>
                {material.isActive ? 'Available' : 'Not available'}
              </StatusBadge>
            </Flex>

            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
              <FieldCard label="Material name" value={material.name} />
              <FieldCard label="Category" value={material.category} />
              <FieldCard label="Unit / quantity" value={material.unit} />
              <FieldCard label="Listing ID" value={material.id != null ? `#${material.id}` : null} />
              <FieldCard label="Created" value={formatDateTime(material.createdAt)} />
              <FieldCard label="Last updated" value={formatDateTime(material.updatedAt)} />
            </SimpleGrid>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} flexShrink={0} borderTopWidth="1px" borderColor="border.subtle">
          {hasImage ? (
            <OutlineButton onClick={() => onViewImage?.(material)}>View image</OutlineButton>
          ) : null}
          <OutlineButton onClick={onClose}>Close</OutlineButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
